/**
 * Новости: полка на главной, лента и экран новости. Ручек на бэкенде пока нет,
 * поэтому по умолчанию данные берутся из mock-хранилища в браузере
 * (`env.newsMock`); когда `GET /news` появится, переключение — один флаг,
 * экраны и запросы остаются теми же.
 */
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import type { ApiClient } from '@dorixona/api'
import {
  NEWS_PAGE_SIZE,
  createHttpNewsBackend,
  createMockNewsStore,
  type NewsBackend,
} from '@dorixona/api/news'
import { nowMs } from '@dorixona/utils'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { env } from '../../config/env'
import { nextPage, toNews, toNewsListPage, type News, type NewsListPage } from './model'

/** Сколько свежих новостей на полке главной. */
export const LATEST_ON_HOME = 5

/**
 * Источник новостей: mock в браузере или настоящие ручки. Mock берёт часы
 * приложения — запланированная новость появится в нём так же, как на сервере.
 */
export function newsBackend(api: ApiClient): NewsBackend {
  return env.newsMock
    ? createMockNewsStore({ storage: localStorage, now: nowMs })
    : createHttpNewsBackend(api)
}

export async function fetchNewsPage(
  api: ApiClient,
  page: number,
  pageSize: number = NEWS_PAGE_SIZE,
): Promise<NewsListPage> {
  return toNewsListPage(await newsBackend(api).published({ page, pageSize }), env.apiBaseUrl)
}

export async function fetchNews(api: ApiClient, id: number): Promise<News> {
  return toNews(await newsBackend(api).publishedOne(id), env.apiBaseUrl)
}

/**
 * Лента страницами, новые сверху: первая порция сразу, следующие — когда
 * покупатель долистал до конца. Экран получает уже склеенный список.
 */
export function useNewsPage() {
  const api = useApi()
  return useInfiniteQuery({
    queryKey: keys.newsList,
    queryFn: ({ pageParam }) => fetchNewsPage(api, pageParam),
    initialPageParam: 1,
    getNextPageParam: nextPage,
    select: (data): NewsListPage => ({
      items: data.pages.flatMap((page) => page.items),
      total: data.pages.reduce((_, page) => page.total, 0),
    }),
  })
}

/** Несколько свежих новостей для полки на главной — одна порция нужного размера. */
export function useLatestNews(limit: number = LATEST_ON_HOME) {
  const api = useApi()
  return useQuery({
    queryKey: keys.newsLatest(limit),
    queryFn: async () => (await fetchNewsPage(api, 1, limit)).items,
  })
}

/** Одна новость с текстом. Неверный номер в адресе запроса не делает. */
export function useNews(id: number) {
  const api = useApi()
  return useQuery({
    queryKey: keys.newsItem(id),
    queryFn: () => fetchNews(api, id),
    enabled: id > 0,
  })
}
